import { createStyles, Group, Stack } from '@mantine/core';
import { Button, Icon, Text, Title } from '@repo/foundations';
import { IconCheck } from '@tabler/icons-react';
import { map, size, startCase, times } from 'lodash-es';
import type {
	PLAN_FEATURE_CATEGORIES,
	PLAN_FEATURE_ITEM,
	PLANS,
} from './constants';
import { getNumOfFeatureItems } from './utils';

interface IPlansCard {
	title: (typeof PLANS)[number];
	color: string;
	bgColor: string;
	description: string;
	descriptionColor?: string;
	ctaButtonLabel: string;
	ctaOnClick: () => void;
	features: {
		[key: (typeof PLAN_FEATURE_CATEGORIES)[number]]: PLAN_FEATURE_ITEM[];
	};
	ctaButtonFilled?: boolean;
	disabled?: boolean;
	pricing?: string;
	gradient?: boolean;
	includesSecodaAI?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{
			color,
			bgColor,
			descriptionColor,
			gradient,
		}: {
			color: string;
			bgColor: string;
			descriptionColor: string;
			gradient?: boolean;
		}
	) => ({
		wrapper: {
			borderRadius: theme.radius.lg,
			borderWidth: 1,
			borderStyle: 'solid',
			borderColor: theme.other.getColor('border/secondary/default'),
			boxShadow: theme.shadows.sm,
			background: gradient
				? 'linear-gradient(180deg, #FFE4E1 0%, #E6E6FA 100%)'
				: bgColor,
		},
		title: {
			color: theme.other.getColor(color),
			fontWeight: 700,
		},
		description: {
			color: theme.other.getColor(descriptionColor),
		},
		item: {
			color: theme.other.getColor(color),
		},
		pricing: {
			color: theme.other.getColor(color),
			fontWeight: 700,
			fontSize: theme.fontSizes.lg,
		},
	})
);

function PlansCard({
	title,
	color,
	bgColor,
	description,
	descriptionColor = color,
	ctaButtonLabel,
	ctaOnClick,
	features,
	ctaButtonFilled,
	disabled = false,
	pricing,
	gradient = false,
	includesSecodaAI = false,
}: IPlansCard) {
	const { classes, theme } = useStyles({
		color,
		bgColor,
		descriptionColor,
		gradient,
	});
	const numOfFeatureCategoryItems = getNumOfFeatureItems();

	return (
		<Stack className={classes.wrapper} spacing="xl" p="xl">
			<Stack spacing="md">
				<Stack spacing="sm">
					<Title className={classes.title} order={1}>
						{title === 'secoda-ai' ? 'Secoda AI' : startCase(title)}
					</Title>
					<Text size="sm" weight="semibold" className={classes.description}>
						{description}
					</Text>
					{pricing && (
						<Text size="sm" weight="bold" className={classes.item}>
							{pricing}
						</Text>
					)}
					{includesSecodaAI && (
						<Text size="sm" weight="bold" className={classes.item}>
							Includes Secoda AI
						</Text>
					)}
				</Stack>
				<Button
					variant={ctaButtonFilled ? 'primary' : 'default'}
					onClick={ctaOnClick}
					disabled={disabled}
				>
					{ctaButtonLabel}
				</Button>
				{map(features, (items, category) => (
					<Stack key={category} spacing="xs">
						<Text size="sm" weight="bold" className={classes.item}>
							{startCase(category)}
						</Text>
						{map(items, (item) => (
							<Group spacing="xs" noWrap>
								<IconCheck
									size={theme.other.iconSize.md}
									className={classes.item}
								/>
								<Text size="sm" lineClamp={1} className={classes.item}>
									{item.label}
								</Text>
							</Group>
						))}
						{times(
							numOfFeatureCategoryItems[category] - size(items),
							(index) => (
								<Group spacing="xs" key={`iconLineDashed-${index}`}>
									<Icon name="lineDashed" color={'icon/primary/disabled'} />
								</Group>
							)
						)}
					</Stack>
				))}
			</Stack>
		</Stack>
	);
}

export default PlansCard;
